import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";

const WASM_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm";
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task";

const EYE_BLINK_MAX = 0.45;
const MIN_EYE_SPAN_RATIO = 0.045;

const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

const getBlendscore = (blendshapes, name) => {
  const list = blendshapes?.[0]?.categories || [];
  const hit = list.find((c) => c.categoryName === name);
  return hit ? hit.score : 1;
};

const evaluateFace = (result) => {
  if (!result?.faceLandmarks?.length) {
    return { ok: false, message: "Face not detected. Look at the camera." };
  }

  if (result.faceLandmarks.length > 1) {
    return { ok: false, message: "Multiple faces detected. Only one face allowed." };
  }

  const landmarks = result.faceLandmarks[0];
  const blinkL = getBlendscore(result.faceBlendshapes, "eyeBlinkLeft");
  const blinkR = getBlendscore(result.faceBlendshapes, "eyeBlinkRight");

  if (blinkL > EYE_BLINK_MAX || blinkR > EYE_BLINK_MAX) {
    return {
      ok: false,
      message: "Keep both eyes open and look at the camera.",
    };
  }

  const leftSpan = dist(landmarks[33], landmarks[133]);
  const rightSpan = dist(landmarks[362], landmarks[263]);
  const faceWidth = dist(landmarks[234], landmarks[454]);

  if (!faceWidth || leftSpan / faceWidth < MIN_EYE_SPAN_RATIO) {
    return { ok: false, message: "Come closer — position your face in the frame." };
  }

  if (rightSpan / faceWidth < MIN_EYE_SPAN_RATIO) {
    return { ok: false, message: "Come closer — position your face in the frame." };
  }

  return {
    ok: true,
    message: "Position your face inside the frame and ensure that the circle is green.",
  };
};

const FwSelfieCamera = forwardRef(({ active, onReadyChange, onHintChange, onError }, ref) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const landmarkerRef = useRef(null);
  const rafRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const sessionRef = useRef(0);
  const onReadyChangeRef = useRef(onReadyChange);
  const onHintChangeRef = useRef(onHintChange);
  const onErrorRef = useRef(onError);

  const [modelsReady, setModelsReady] = useState(false);
  const [faceReady, setFaceReady] = useState(false);

  useImperativeHandle(ref, () => ({
    capturePhoto: () => {
      const video = videoRef.current;
      if (!video || video.readyState < 2 || !video.videoWidth) return "";

      const canvas = document.createElement("canvas");
      const size = Math.min(video.videoWidth, video.videoHeight);
      const sx = (video.videoWidth - size) / 2;
      const sy = (video.videoHeight - size) / 2;
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) return "";

      // Mirror horizontally to match selfie preview
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
      return canvas.toDataURL("image/jpeg", 0.92);
    },
  }));

  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
    onHintChangeRef.current = onHintChange;
    onErrorRef.current = onError;
  }, [onReadyChange, onHintChange, onError]);

  useEffect(() => {
    let cancelled = false;

    const loadModel = async () => {
      try {
        onHintChangeRef.current?.("Opening camera…");
        const vision = await FilesetResolver.forVisionTasks(WASM_URL);
        const landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO",
          numFaces: 1,
          outputFaceBlendshapes: true,
        }).catch(async () =>
          FaceLandmarker.createFromOptions(vision, {
            baseOptions: { modelAssetPath: MODEL_URL, delegate: "CPU" },
            runningMode: "VIDEO",
            numFaces: 1,
            outputFaceBlendshapes: true,
          })
        );

        if (cancelled) {
          landmarker.close();
          return;
        }

        landmarkerRef.current = landmarker;
        setModelsReady(true);
        onHintChangeRef.current?.(
          "Position your face inside the frame and ensure that the circle is green."
        );
      } catch {
        if (!cancelled) {
          // Camera can still work; only face-check is unavailable.
          onErrorRef.current?.(
            "Face check failed to load. You can still preview the camera — please refresh to retry face check."
          );
          onHintChangeRef.current?.("Camera preview ready. Face check unavailable.");
        }
      }
    };

    loadModel();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (landmarkerRef.current) {
        landmarkerRef.current.close();
        landmarkerRef.current = null;
      }
    };
  }, []);

  const stopCamera = useCallback(() => {
    sessionRef.current += 1;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setFaceReady(false);
    onReadyChangeRef.current?.(false);
  }, []);

  const runFaceLoop = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;

    if (!video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(runFaceLoop);
      return;
    }

    if (!landmarker) {
      rafRef.current = requestAnimationFrame(runFaceLoop);
      return;
    }

    if (video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const result = landmarker.detectForVideo(video, performance.now());
        const verdict = evaluateFace(result);
        setFaceReady(verdict.ok);
        onReadyChangeRef.current?.(verdict.ok);
        onHintChangeRef.current?.(verdict.message);
      } catch {
        // ignore frame errors
      }
    }

    rafRef.current = requestAnimationFrame(runFaceLoop);
  }, []);

  const startCamera = useCallback(async () => {
    const session = ++sessionRef.current;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("unsupported");
      }
      if (typeof window !== "undefined" && !window.isSecureContext) {
        onErrorRef.current?.(
          "Camera needs HTTPS or localhost. Open the app on a secure URL."
        );
        onHintChangeRef.current?.("Camera blocked on insecure connection.");
        return;
      }

      onHintChangeRef.current?.("Allow camera permission…");
      onErrorRef.current?.("");

      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "user" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      });

      if (session !== sessionRef.current) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = mediaStream;
      const video = videoRef.current;
      if (!video) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      video.srcObject = mediaStream;
      video.muted = true;
      video.setAttribute("playsinline", "true");
      video.setAttribute("webkit-playsinline", "true");

      try {
        await video.play();
      } catch {
        // Autoplay can fail briefly; stream is still attached.
      }

      if (session !== sessionRef.current) return;

      lastVideoTimeRef.current = -1;
      onHintChangeRef.current?.(
        landmarkerRef.current
          ? "Position your face inside the frame and ensure that the circle is green."
          : "Camera opened. Loading face check…"
      );
      rafRef.current = requestAnimationFrame(runFaceLoop);
    } catch (err) {
      if (session !== sessionRef.current) return;
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        onErrorRef.current?.("Camera access denied. Please allow camera permission.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        onErrorRef.current?.("No camera found on this device.");
      } else if (err?.message === "unsupported") {
        onErrorRef.current?.("Camera is not supported in this browser.");
      } else {
        onErrorRef.current?.("Unable to open camera. Please check permissions and try again.");
      }
      onReadyChangeRef.current?.(false);
      onHintChangeRef.current?.("Camera preview unavailable.");
    }
  }, [runFaceLoop]);

  // Open camera as soon as screen is active (don't wait for MediaPipe).
  useEffect(() => {
    if (active) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => stopCamera();
  }, [active, startCamera, stopCamera]);

  // When model finishes loading while camera is already on, keep the loop running.
  useEffect(() => {
    if (active && modelsReady && streamRef.current && !rafRef.current) {
      lastVideoTimeRef.current = -1;
      rafRef.current = requestAnimationFrame(runFaceLoop);
    }
  }, [active, modelsReady, runFaceLoop]);

  return (
    <div className={`fw-selfie-ring ${faceReady ? "is-ready" : ""}`}>
      <video
        ref={videoRef}
        className="fw-selfie-ring__video"
        autoPlay
        playsInline
        muted
      />
    </div>
  );
});

FwSelfieCamera.displayName = "FwSelfieCamera";

export default FwSelfieCamera;
